import { useState } from 'react';
import { Box, Button, Card, CardContent, CardHeader, CircularProgress, Divider, Stack, Tab, Tabs, TextField, Tooltip, Typography } from '@mui/material';
import { useCart } from '../../contexts/CartContext';
import ActionConfirmation from '../elements/ActionConfirmation';
import removeCoupon from '../../hooks/coupon/removeCoupon';
import type { ActionConfirmationVariant, PaymentSummary } from '../../types';
import { CartSummaryProps } from '../../types';
import { EVENTS_THEME_BACKGROUND_COLOR, KEY_COUPON_LOCATION_ID_ALL } from '../../constants';
import { calculateTotalCouponDiscount, formatCurrencyDisplay } from '../../utils';
import InfoIcon from '@mui/icons-material/Info';
import { useScrollToConfirmation } from '../../hooks/useScrollToConfirmation';

const CartSummary: React.FC<CartSummaryProps> = ({ applyCouponCode, readonly, checkout, onPayDeposit, onPayFull, buttonDisabled }) => {
  const { checkoutPaymentSummary, coupon, applyCoupon, fetchCart, cartItems: rawCartItems, isApplyingProcessingFee } = useCart();
  
  const [couponCode, setCouponCode] = useState('');
  const [showCouponInput, setShowCouponInput] = useState(false);
  const [isApplyingCoupon, setIsApplyingCoupon] = useState(false);
  const [isRemovingCoupon, setIsRemovingCoupon] = useState(false);
  const [confirmation, setConfirmation] = useState<{ visible: boolean; variant: ActionConfirmationVariant; message: string }>({ visible: false, variant: 'success', message: '' });
  const [activeTab, setActiveTab] = useState<'payInFull' | 'payDeposit'>('payInFull');

  const handleApplyCoupon = async () => {
    if (!showCouponInput) {
      setShowCouponInput(true);
      return;
    }

    setIsApplyingCoupon(true);
    try {
      if (applyCouponCode) {
        const success = await applyCouponCode(couponCode);

        if (success) {
          setIsApplyingCoupon(false);
          setShowCouponInput(false);
        }
      }
    } catch (error) {
      setIsApplyingCoupon(false);
      setConfirmation({ visible: true, variant: 'error', message: error instanceof Error ? error.message : 'Failed to apply coupon.' });
    } finally {
      setIsApplyingCoupon(false);
    }
  };

  const handleRemoveCoupon = async () => {
    setIsRemovingCoupon(true);
    try {
      const response = await removeCoupon();
      if (response.success) {
        setIsRemovingCoupon(false);
        applyCoupon({ code: null, value: null, type: null, locationId: null, locationName: null });
        setConfirmation({ visible: true, variant: 'success', message: 'Coupon removed successfully.' });
        setShowCouponInput(false);
        setCouponCode('');
        await fetchCart();
      } else {
        setIsRemovingCoupon(false);
        setConfirmation({ visible: true, variant: 'error', message: response.message || 'Failed to remove coupon.' });
      }
    } catch (error) {
      setIsRemovingCoupon(false);
      setConfirmation({ visible: true, variant: 'error', message: 'An error occurred while removing the coupon.' });
    }
  };

  const handleDismissConfirmation = () => {
    setConfirmation((c) => ({ ...c, visible: false }));
  };

  useScrollToConfirmation(confirmation.visible, confirmation.message, 'cart-summary-confirmation');

  const handleTabChange = (_event: React.SyntheticEvent, newValue: 'payInFull' | 'payDeposit') => {
    setActiveTab(newValue);
  };

  // Pass the raw cart items (with .data property) to the discount calculator
  const totalDiscount = calculateTotalCouponDiscount(rawCartItems);
  const isCouponLoading = isApplyingCoupon || isRemovingCoupon;
  const backendSummary: PaymentSummary | null = checkout ? checkoutPaymentSummary : null;
  const cartSummary = !checkout ? (rawCartItems as any)?.summary : null;
  const cartPayInFullSummary: PaymentSummary | null = cartSummary?.payInFull ?? null;
  const cartPayDepositSummary: PaymentSummary | null = cartSummary?.payDeposit ?? null;
  const activeCartSummary: PaymentSummary | null =
    activeTab === 'payDeposit'
      ? (cartPayDepositSummary ?? cartPayInFullSummary)
      : cartPayInFullSummary;

  return (
    <Card
      elevation={4}
      sx={{
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        borderLeft: `4px solid ${EVENTS_THEME_BACKGROUND_COLOR}`,
        backgroundColor: 'background.paper',
        minHeight: '460px',
      }}
    >
      <CardHeader
        title={checkout ? "Payment Summary" : "Order Summary"}
        titleTypographyProps={{ variant: 'h5', fontWeight: 600, fontSize: '1.5rem' }}
        sx={{
          backgroundColor: 'rgba(41, 170, 225, 0.05)',
          borderBottom: '1px solid rgba(41, 170, 225, 0.1)',
          py: 2,
        }}
      />
      {/* Tabs - Only show on cart page when both payment options are available */}
      {!checkout && onPayDeposit && onPayFull && cartPayDepositSummary != null && (
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="fullWidth"
          sx={{
            borderBottom: '1px solid rgba(41, 170, 225, 0.1)',
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.95rem',
            },
            '& .Mui-selected': {
              color: EVENTS_THEME_BACKGROUND_COLOR,
            },
          }}
        >
          <Tab label="Pay In Full" value="payInFull" />
          <Tab label="Pay Deposit" value="payDeposit" />
        </Tabs>
      )}
      <CardContent sx={{ pt: 3.5, px: 2.5, pb: 3.5 }}>
        <Stack spacing={2.5} className="cart-summary">
          {/* Pricing Breakdown */}
          <Box>
            <Stack spacing={2}>
              {/* Show breakdown based on page and order type */}
              {!checkout && activeCartSummary && (
                <>
                  {activeCartSummary.initialTotal > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="h6" color="text.secondary" sx={{ fontSize: '1rem', fontWeight: 500 }}>
                        Total Fees
                      </Typography>
                      <Typography variant="h5" fontWeight={600} sx={{ fontSize: '1.4rem' }}>
                        ${formatCurrencyDisplay(activeCartSummary.initialTotal)}
                      </Typography>
                    </Box>
                  )}

                  {activeTab === 'payDeposit' && (activeCartSummary.depositsAmount ?? 0) > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="h6" color="text.secondary" sx={{ fontSize: '1rem', fontWeight: 500 }}>
                        Deposits
                      </Typography>
                      <Typography variant="h5" fontWeight={600} sx={{ fontSize: '1.4rem' }}>
                        -${formatCurrencyDisplay(activeCartSummary.depositsAmount)}
                      </Typography>
                    </Box>
                  )}

                  {activeTab === 'payDeposit' && (activeCartSummary.fullPaymentItemsAmount ?? 0) > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="h6" color="text.secondary" sx={{ fontSize: '1rem', fontWeight: 500 }}>
                        Full Payment Items
                      </Typography>
                      <Typography variant="h5" fontWeight={600} sx={{ fontSize: '1.4rem' }}>
                        -${formatCurrencyDisplay(activeCartSummary.fullPaymentItemsAmount)}
                      </Typography>
                    </Box>
                  )}

                  {activeCartSummary.couponDiscountTotal > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="h6" color="success.main" sx={{ fontSize: '1rem', fontWeight: 500 }}>
                        Coupon Discount
                      </Typography>
                      <Typography variant="h5" fontWeight={600} color="success.main" sx={{ fontSize: '1.4rem' }}>
                        -${formatCurrencyDisplay(activeCartSummary.couponDiscountTotal)}
                      </Typography>
                    </Box>
                  )}

                  {activeCartSummary.paidInFullDiscountTotal > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="h6" color="success.main" sx={{ fontSize: '1rem', fontWeight: 500 }}>
                        Paid in Full Discount
                      </Typography>
                      <Typography variant="h5" fontWeight={600} color="success.main" sx={{ fontSize: '1.4rem' }}>
                        -${formatCurrencyDisplay(activeCartSummary.paidInFullDiscountTotal)}
                      </Typography>
                    </Box>
                  )}

                  <Divider sx={{ my: 1 }} />

                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="h6" fontWeight={600} sx={{ fontSize: '1.1rem' }}>
                      {activeTab === 'payDeposit' ? 'Due Today' : 'Pay In Full Today'}
                    </Typography>
                    <Typography variant="h5" fontWeight={700} sx={{ fontSize: '1.4rem', color: EVENTS_THEME_BACKGROUND_COLOR }}>
                      ${formatCurrencyDisplay(activeCartSummary.dueTodayAfterDiscounts)}
                    </Typography>
                  </Box>

                  {activeCartSummary.remainingBalance > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Box>
                        <Typography variant="h6" color="text.secondary" sx={{ fontSize: '1rem', fontWeight: 500, lineHeight: 1.2, mb: 0 }}>
                          Remaining Balance
                        </Typography>
                        {activeCartSummary.chargedOn && (
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', mt: 0.25, lineHeight: 1 }}>
                            Charged on {new Date(activeCartSummary.chargedOn).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })}
                          </Typography>
                        )}
                      </Box>
                      <Typography variant="h5" fontWeight={600} sx={{ fontSize: '1.4rem' }}>
                        ${formatCurrencyDisplay(activeCartSummary.remainingBalance)}
                      </Typography>
                    </Box>
                  )}

                  {activeCartSummary.processingFee?.amount > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="h6" color="text.secondary" sx={{ fontSize: '1rem', fontWeight: 500 }}>
                        Processing Fee{activeCartSummary.processingFee?.description ? ` (${activeCartSummary.processingFee.description})` : ''}
                      </Typography>
                      <Typography variant="h5" fontWeight={600} sx={{ fontSize: '1.4rem' }}>
                        ${formatCurrencyDisplay(activeCartSummary.processingFee.amount)}
                      </Typography>
                    </Box>
                  )}

                  <Divider />

                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="h6" fontWeight={700} sx={{ fontSize: '1.15rem' }}>
                      Total Due Today
                    </Typography>
                    <Typography variant="h5" fontWeight={700} sx={{ fontSize: '1.4rem' }}>
                      ${formatCurrencyDisplay(activeCartSummary.totalDueToday)}
                    </Typography>
                  </Box>
                </>
              )}



              {checkout && backendSummary && (
                <>
                  {backendSummary.initialTotal > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="h6" color="text.secondary" sx={{ fontSize: '1rem', fontWeight: 500 }}>
                        Total Fees
                      </Typography>
                      <Typography variant="h5" fontWeight={600} sx={{ fontSize: '1.4rem' }}>
                        ${formatCurrencyDisplay(backendSummary.initialTotal)}
                      </Typography>
                    </Box>
                  )}

                  {backendSummary.orderPaymentType === 'deposit' && (backendSummary.depositsAmount ?? 0) > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="h6" color="text.secondary" sx={{ fontSize: '1rem', fontWeight: 500 }}>
                        Deposits
                      </Typography>
                      <Typography variant="h5" fontWeight={600} sx={{ fontSize: '1.4rem' }}>
                        -${formatCurrencyDisplay(backendSummary.depositsAmount)}
                      </Typography>
                    </Box>
                  )}

                  {backendSummary.orderPaymentType === 'deposit' && (backendSummary.fullPaymentItemsAmount ?? 0) > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="h6" color="text.secondary" sx={{ fontSize: '1rem', fontWeight: 500 }}>
                        Full Payment Items
                      </Typography>
                      <Typography variant="h5" fontWeight={600} sx={{ fontSize: '1.4rem' }}>
                        -${formatCurrencyDisplay(backendSummary.fullPaymentItemsAmount)}
                      </Typography>
                    </Box>
                  )}

                  {backendSummary.couponDiscountTotal > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="h6" color="success.main" sx={{ fontSize: '1rem', fontWeight: 500 }}>
                        {backendSummary.orderPaymentType === 'deposit' ? 'Coupon (Applied to Balance)' : 'Coupon Discount'}
                      </Typography>
                      <Typography variant="h5" fontWeight={600} color="success.main" sx={{ fontSize: '1.4rem' }}>
                        -${formatCurrencyDisplay(backendSummary.couponDiscountTotal)}
                      </Typography>
                    </Box>
                  )}

                  {backendSummary.paidInFullDiscountTotal > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="h6" color="success.main" sx={{ fontSize: '1rem', fontWeight: 500 }}>
                        Paid in full discount
                      </Typography>
                      <Typography variant="h5" fontWeight={600} color="success.main" sx={{ fontSize: '1.4rem' }}>
                        -${formatCurrencyDisplay(backendSummary.paidInFullDiscountTotal)}
                      </Typography>
                    </Box>
                  )}

                  {backendSummary.remainingBalance > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Box>
                        <Typography variant="h6" color="text.secondary" sx={{ fontSize: '1rem', fontWeight: 500, lineHeight: 1.2, mb: 0 }}>
                          Remaining Balance
                        </Typography>
                        {backendSummary.chargedOn && (
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', mt: 0.25, lineHeight: 1 }}>
                            Charged on {new Date(backendSummary.chargedOn).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })}
                          </Typography>
                        )}
                      </Box>
                      <Typography variant="h5" fontWeight={600} sx={{ fontSize: '1.4rem' }}>
                        ${formatCurrencyDisplay(backendSummary.remainingBalance)}
                      </Typography>
                    </Box>
                  )}

                  <Divider sx={{ my: 2 }} />

                  <Typography variant="subtitle2" color="text.secondary" sx={{ fontSize: '0.85rem', fontWeight: 700, mb: 1.5, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    Due Today
                  </Typography>

                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="h6" color="text.secondary" sx={{ fontSize: '1rem', fontWeight: 500 }}>
                      {backendSummary.orderPaymentType === 'deposit' ? 'Deposits + Full Payment Items' : 'Full Payment Items'}
                    </Typography>
                    <Typography variant="h5" fontWeight={600} sx={{ fontSize: '1.4rem' }}>
                      ${formatCurrencyDisplay(backendSummary.dueTodayAfterDiscounts)}
                    </Typography>
                  </Box>

                  {backendSummary.processingFee?.amount > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="h6" color="text.secondary" sx={{ fontSize: '1rem', fontWeight: 500 }}>
                        Processing Fee{backendSummary.processingFee?.description ? ` (${backendSummary.processingFee.description})` : ''}
                      </Typography>
                      <Typography variant="h5" fontWeight={600} sx={{ fontSize: '1.4rem' }}>
                        ${formatCurrencyDisplay(backendSummary.processingFee.amount)}
                      </Typography>
                    </Box>
                  )}

                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="h6" fontWeight={700} sx={{ fontSize: '1.15rem' }}>
                      Total Due Today
                    </Typography>
                    <Typography variant="h5" fontWeight={700} sx={{ fontSize: '1.4rem', color: EVENTS_THEME_BACKGROUND_COLOR }}>
                      ${formatCurrencyDisplay(backendSummary.totalDueToday)}
                    </Typography>
                  </Box>
                </>
              )}

              {checkout && !backendSummary && isApplyingProcessingFee && (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 1,
                    py: 5,
                  }}
                >
                  <CircularProgress size={24} />
                  <Typography variant="body2" color="text.secondary">
                    Loading payment summary...
                  </Typography>
                </Box>
              )}

            </Stack>
          </Box>

          <Divider />

          {/* Coupon Section - MOVED ABOVE PAYMENT BUTTONS */}
          {!readonly && (
            <Box>
              {isCouponLoading ? (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 1.5,
                    backgroundColor: 'rgba(136, 183, 62, 0.08)',
                    borderRadius: 1,
                    p: 2,
                    border: '1px solid rgba(136, 183, 62, 0.2)',
                  }}
                >
                  <CircularProgress size={20} sx={{ color: 'success.main' }} />
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.9rem' }}>
                    {isRemovingCoupon ? 'Removing coupon…' : 'Applying coupon…'}
                  </Typography>
                </Box>
              ) : coupon?.code ? (
                <Stack spacing={1}>
                  <Box 
                    sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center',
                      backgroundColor: 'rgba(136, 183, 62, 0.08)',
                      borderRadius: 1,
                      p: 1.5,
                      border: '1px solid rgba(136, 183, 62, 0.2)',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography variant="body1" color="success.main" fontWeight={600} sx={{ fontSize: '0.9rem' }}>
                        <span style={{ color: '#000', fontSize: '0.9rem' }}>Coupon:</span> {coupon.code}
                      </Typography>
                      {coupon && coupon.locationId !== KEY_COUPON_LOCATION_ID_ALL && (
                        <Tooltip
                          title={`This coupon only applies to ${coupon.locationName || 'specified'} location`}
                          arrow
                        >
                          <InfoIcon sx={{ fontSize: 16, color: 'success.main' }} />
                        </Tooltip>
                      )}
                    </Box>
                    <Button 
                      size="small" 
                      color="error" 
                      onClick={handleRemoveCoupon} 
                      disabled={isRemovingCoupon}
                      sx={{ textTransform: 'none', fontSize: '0.85rem', maxHeight: '17px' }}
                    >
                      Remove
                    </Button>
                  </Box>
                  {/* Coupon Discount Line Item */}
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      backgroundColor: 'rgba(136, 183, 62, 0.1)',
                      borderRadius: 1,
                      height: '57px',
                      px: 1.5,
                      border: '1px solid rgba(136, 183, 62, 0.2)',
                    }}
                  >
                    <Typography variant="body2" fontWeight={500} sx={{ fontSize: '0.9rem' }}>
                      Coupon Discount
                    </Typography>
                    <Typography variant="body1" fontWeight={600} color="success.main" sx={{ fontSize: '1rem' }}>
                      -${totalDiscount.toFixed(2)}
                    </Typography>
                  </Box>
                </Stack>
              ) : (
                <Box>
                  {showCouponInput ? (
                    <Stack direction="row" spacing={1} alignItems="center">
                      <TextField
                        fullWidth
                        size="small"
                        variant="outlined"
                        label="Enter Coupon Code"
                        value={couponCode}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          setCouponCode(e.target.value.toUpperCase())
                        }
                      />
                      <Button
                        variant="contained"
                        onClick={handleApplyCoupon}
                        disabled={isApplyingCoupon}
                        sx={{ minWidth: '80px', fontSize: '0.9rem', height: '40px', marginTop: '6px !important' }}
                      >
                        {isApplyingCoupon ? <CircularProgress size={10} /> : 'Apply'}
                      </Button>
                    </Stack>
                  ) : (
                    <Button
                      variant="outlined"
                      fullWidth
                      onClick={handleApplyCoupon}
                      sx={{ textTransform: 'none' }}
                    >
                      Add Coupon Code
                    </Button>
                  )}
                </Box>
              )}
            </Box>
          )}

          {/* Payment Buttons */}
          {!checkout && onPayDeposit && onPayFull && (
            <>
              <Divider />
              
              <Stack spacing={2}>
                {/* Primary Action Button */}
                {activeTab === 'payInFull' ? (
                  <Button
                    variant="contained"
                    fullWidth
                    size="large"
                    onClick={onPayFull}
                    disabled={buttonDisabled}
                    sx={{
                      backgroundColor: '#88B73E',
                      color: 'white',
                      py: 1.5,
                      fontSize: '1rem',
                      fontWeight: 600,
                      '&:hover': { backgroundColor: '#7aa635' },
                    }}
                  >
                    Pay in Full - ${formatCurrencyDisplay(cartPayInFullSummary?.totalDueToday)}
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    fullWidth
                    size="large"
                    onClick={onPayDeposit}
                    disabled={buttonDisabled}
                    sx={{
                      backgroundColor: EVENTS_THEME_BACKGROUND_COLOR,
                      color: 'white',
                      py: 1.5,
                      fontSize: '1rem',
                      fontWeight: 600,
                      '&:hover': { backgroundColor: '#2199cc' },
                    }}
                  >
                    Pay Deposit - ${formatCurrencyDisplay(cartPayDepositSummary?.totalDueToday)}
                  </Button>
                )}

                {/* Secondary Switch Button */}
                {cartPayDepositSummary != null && (
                  activeTab === 'payInFull' ? (
                    <Button
                      variant="outlined"
                      fullWidth
                      size="large"
                      onClick={() => setActiveTab('payDeposit')}
                      disabled={buttonDisabled}
                      sx={{
                        borderColor: EVENTS_THEME_BACKGROUND_COLOR,
                        color: EVENTS_THEME_BACKGROUND_COLOR,
                        py: 1.5,
                        fontSize: '1rem',
                        fontWeight: 600,
                        '&:hover': {
                          borderColor: '#2199cc',
                          backgroundColor: 'rgba(41, 170, 225, 0.08)',
                        },
                      }}
                    >
                      Pay Deposit
                    </Button>
                  ) : (
                    <Button
                      variant="outlined"
                      fullWidth
                      size="large"
                      onClick={() => setActiveTab('payInFull')}
                      disabled={buttonDisabled}
                      sx={{
                        borderColor: '#88B73E',
                        color: '#88B73E',
                        py: 1.5,
                        fontSize: '1rem',
                        fontWeight: 600,
                        '&:hover': {
                          borderColor: '#7aa635',
                          backgroundColor: 'rgba(136, 183, 62, 0.08)',
                        },
                      }}
                    >
                      Pay In Full
                    </Button>
                  )
                )}
              </Stack>
            </>
          )}

          <ActionConfirmation
            id="cart-summary-confirmation"
            visible={confirmation.visible}
            variant={confirmation.variant}
            message={confirmation.message}
            onDismiss={handleDismissConfirmation}
          />
        </Stack>
      </CardContent>
    </Card>
  );
};


export default CartSummary;
